import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, ValidationModule, PaginationModule, TextFilterModule } from "ag-grid-community";
import { ClientSideRowModelModule, ClientSideRowModelApiModule } from "ag-grid-community";

import TabLayout from "../../components/TabLayout";

import { accounts } from "../../utils/appConstants";
import api from "../../utils/api";

ModuleRegistry.registerModules([ClientSideRowModelModule, ClientSideRowModelApiModule, ValidationModule, PaginationModule, TextFilterModule]);

const Ledger = () => {
	const [filteredTotal, setFilteredTotal] = useState(0);
	const gridRef = useRef();

	const [moneyLineItems, setMoneyLineItems] = useState([]);
	const [columnDefs, setColumnDefs] = useState([]);
	const [monthlyTotals, setMonthlyTotals] = useState({});
	const [businesses, setBusinesses] = useState([]);

	const [accountBalances, setAccountBalances] = useState([]);

	useEffect(() => {
		const fetchMonthlyData = async () => {
			try {
				const res = await api.get(`/money/AccountBalance/`);
				setAccountBalances(res.data);
				const currentBalances = {};
				for (const account of accounts) {
					const newestRecord = res.data.reduce((newest, current) => {
						return current.account === account && (!newest || current.date > newest.date) ? current : newest;
					}, null);
					currentBalances[account] = newestRecord ? newestRecord.value : 0;
				}
				console.log(currentBalances);
				setAccountBalances(currentBalances);
			} catch (err) {
				console.log(err);
			}
		};
		fetchMonthlyData();
	}, []);

	useEffect(() => {
		const fetchMoneyData = async () => {
			try {
				const res = await api.get(`/money/lineItems`);
				setMoneyLineItems(res.data);

				const businessSet = new Set(res.data.map((item) => item.business));
				const uniqueBusinesses = Array.from(businessSet);

				const businessData = {};

				res.data.forEach((item) => {
					const { business, category, date, transaction } = item;
					if (!businessData[business]) {
						businessData[business] = {
							entries: [],
							categories: new Set(),
							monthlyTotals: {},
						};
					}

					businessData[business].entries.push(item);
					businessData[business].categories.add(category);

					const monthKey = new Date(date).toISOString().slice(0, 7);

					if (!businessData[business].monthlyTotals[category]) {
						businessData[business].monthlyTotals[category] = {};
					}

					if (!businessData[business].monthlyTotals[category][monthKey]) {
						businessData[business].monthlyTotals[category][monthKey] = 0;
					}

					businessData[business].monthlyTotals[category][monthKey] += Number(transaction || 0);
				});

				Object.values(businessData).forEach((b) => {
					Object.keys(b.monthlyTotals).forEach((category) => {
						const months = Object.keys(b.monthlyTotals[category]);
						const years = new Set(months.map((m) => m.split("-")[0]));
						years.forEach((year) => {
							const allMonths = Array.from({ length: 12 }, (_, k) => `${year}-${(k + 1).toString().padStart(2, "0")}`);
							allMonths.forEach((month) => {
								if (!b.monthlyTotals[category][month]) {
									b.monthlyTotals[category][month] = 0;
								}
							});
						});
					});
					b.categories = Array.from(b.categories);
				});

				setMonthlyTotals(businessData);
				setBusinesses(uniqueBusinesses);

				const columnDefs = [
					{ headerName: "ID", field: "accId", sortable: true, filter: true },
					{
						headerName: "Date",
						field: "date",
						sortable: true,
						filter: true,
						valueFormatter: (params) => {
							const date = new Date(params.value);
							return date.toLocaleDateString("en-US");
						},
					},
					{
						headerName: "Transaction",
						field: "transaction",
						sortable: true,
						filter: true,
						valueFormatter: (params) => {
							const value = Number(params.value);
							if (isNaN(value)) return "-";
							return value.toLocaleString("en-US", {
								style: "currency",
								currency: "USD",
								minimumFractionDigits: 2,
							});
						},
					},
					{ headerName: "Description", field: "description", sortable: true, filter: true },
					{ headerName: "Category", field: "category", sortable: true, filter: true },
					{ headerName: "Business", field: "business", sortable: true, filter: true },
					{ headerName: "Account", field: "account", sortable: true, filter: true },
				];

				setColumnDefs(columnDefs);
			} catch (err) {
				console.error("Failed to fetch data", err);
			}
		};

		fetchMoneyData();
	}, []);

	const onGridReady = () => {
		updateFilteredTotal();
	};

	const updateFilteredTotal = () => {
		const api = gridRef.current?.api;
		if (!api) return;

		let sum = 0;
		api.forEachNodeAfterFilterAndSort((node) => {
			sum += Number(node.data.transaction || 0);
		});
		setFilteredTotal(sum);
	};

	const prioritized = ["NL", "Immerge", "Home", "Apartment"];
	const otherBusinesses = businesses.filter((b) => !prioritized.includes(b));

	const tabContent = [
		...prioritized
			.filter((b) => businesses.includes(b))
			.map((business) => ({
				title: business,
				content: moneyLineItems.length > 0 ? <MonthlyTotalsTable selectedBusiness={business} businessData={monthlyTotals} /> : null,
			})),
		{
			title: "Other",
			content: moneyLineItems.length > 0 ? <CombinedBusinessesTable selectedBusinesses={otherBusinesses} businessData={monthlyTotals} /> : null,
		},
	];

	return (
		<div className="lavella page ledger">
			<section>
				<div className="cont--lg">
					{moneyLineItems.length > 0 && (
						<>
							<div className="grid-container">
								<AgGridReact ref={gridRef} rowData={moneyLineItems} columnDefs={columnDefs} pagination={false} paginationPageSize={10} floatingFilter={true} onGridReady={onGridReady} onFilterChanged={updateFilteredTotal} />
							</div>
							<div className="total-footer">
								<p>
									Total Amount:{" "}
									{filteredTotal.toLocaleString("en-US", {
										style: "currency",
										currency: "USD",
										minimumFractionDigits: 2,
									})}
								</p>
							</div>
						</>
					)}
				</div>
			</section>
		</div>
	);
};

const MonthlyTotalsTable = ({ selectedBusiness, businessData }) => {
	if (!businessData[selectedBusiness]) return null;

	const monthlyTotals = businessData[selectedBusiness].monthlyTotals;
	const sampleCategory = Object.keys(monthlyTotals)[0];
	const sortedMonths = Object.keys(monthlyTotals[sampleCategory]).sort();

	const formattedMonthNames = sortedMonths.map((monthKey) => {
		const [_, month] = monthKey.split("-");
		const date = new Date(0, month - 1);
		return date.toLocaleString("en-US", { month: "short" });
	});

	const monthlyTotalsRow = sortedMonths.map((month) => {
		let sum = 0;
		Object.values(monthlyTotals).forEach((categoryData) => {
			sum += categoryData[month] || 0;
		});
		return sum;
	});

	const grandTotal = monthlyTotalsRow.reduce((sum, val) => sum + val, 0);
	const grandAverage = grandTotal / 12;

	return (
		<div className="monthly-totals">
			<div className="headers">
				<p>Category</p>
				{formattedMonthNames.map((monthName, idx) => (
					<p key={idx}>{monthName}</p>
				))}
				<p>Total</p>
				<p>Avg (12m)</p>
			</div>

			{Object.entries(monthlyTotals).map(([category, monthlyData]) => {
				const values = sortedMonths.map((month) => monthlyData[month] || 0);
				const total = values.reduce((sum, val) => sum + val, 0);
				const average = total / 12;

				return (
					<div key={category} className="row">
						<p>{category}</p>
						{values.map((val, idx) => (
							<p className={val !== 0 ? "active sum" : "sum"} key={idx}>
								{val.toLocaleString("en-US", {
									style: "currency",
									currency: "USD",
									minimumFractionDigits: 2,
								})}
							</p>
						))}
						<p>
							{total.toLocaleString("en-US", {
								style: "currency",
								currency: "USD",
								minimumFractionDigits: 2,
							})}
						</p>
						<p>
							{average.toLocaleString("en-US", {
								style: "currency",
								currency: "USD",
								minimumFractionDigits: 2,
							})}
						</p>
					</div>
				);
			})}

			<div className="footer-row">
				<p>
					<strong>Monthly Totals</strong>
				</p>
				{monthlyTotalsRow.map((val, idx) => (
					<p key={idx}>
						{val.toLocaleString("en-US", {
							style: "currency",
							currency: "USD",
							minimumFractionDigits: 2,
						})}
					</p>
				))}
				<p>
					{grandTotal.toLocaleString("en-US", {
						style: "currency",
						currency: "USD",
						minimumFractionDigits: 2,
					})}
				</p>
				<p>
					{grandAverage.toLocaleString("en-US", {
						style: "currency",
						currency: "USD",
						minimumFractionDigits: 2,
					})}
				</p>
			</div>
		</div>
	);
};

const CombinedBusinessesTable = ({ selectedBusinesses, businessData }) => {
	if (!selectedBusinesses || selectedBusinesses.length === 0) return null;

	const allMonthsSet = new Set();
	selectedBusinesses.forEach((biz) => {
		const monthlyTotals = businessData[biz]?.monthlyTotals || {};
		Object.values(monthlyTotals).forEach((monthlyData) => {
			Object.keys(monthlyData).forEach((month) => allMonthsSet.add(month));
		});
	});

	const sortedMonths = Array.from(allMonthsSet).sort();
	const formattedMonthNames = sortedMonths.map((monthKey) => {
		const [_, month] = monthKey.split("-");
		const date = new Date(0, month - 1);
		return date.toLocaleString("en-US", { month: "short" });
	});

	const rows = selectedBusinesses.map((biz) => {
		const monthlyTotals = businessData[biz]?.monthlyTotals || {};
		const rowData = sortedMonths.map((month) => {
			let total = 0;
			Object.values(monthlyTotals).forEach((monthlyData) => {
				total += monthlyData[month] || 0;
			});
			return total;
		});
		const total = rowData.reduce((sum, val) => sum + val, 0);
		const avg = total / 12;
		return { label: biz, values: rowData, total, avg };
	});

	const monthlyTotalsRow = sortedMonths.map((month, idx) => rows.reduce((sum, row) => sum + row.values[idx], 0));

	const grandTotal = monthlyTotalsRow.reduce((sum, val) => sum + val, 0);
	const grandAverage = grandTotal / 12;

	return (
		<div className="monthly-totals">
			<div className="headers">
				<p>Business</p>
				{formattedMonthNames.map((monthName, idx) => (
					<p key={idx}>{monthName}</p>
				))}
				<p>Total</p>
				<p>Avg (12m)</p>
			</div>

			{rows.map((row) => (
				<div key={row.label} className="row">
					<p>{row.label}</p>
					{row.values.map((val, idx) => (
						<p className={val !== 0 ? "active sum" : "sum"} key={idx}>
							{val.toLocaleString("en-US", {
								style: "currency",
								currency: "USD",
								minimumFractionDigits: 2,
							})}
						</p>
					))}
					<p>
						{row.total.toLocaleString("en-US", {
							style: "currency",
							currency: "USD",
							minimumFractionDigits: 2,
						})}
					</p>
					<p>
						{row.avg.toLocaleString("en-US", {
							style: "currency",
							currency: "USD",
							minimumFractionDigits: 2,
						})}
					</p>
				</div>
			))}

			<div className="footer-row">
				<p>
					<strong>Monthly Totals</strong>
				</p>
				{monthlyTotalsRow.map((val, idx) => (
					<p key={idx}>
						{val.toLocaleString("en-US", {
							style: "currency",
							currency: "USD",
							minimumFractionDigits: 2,
						})}
					</p>
				))}
				<p>
					{grandTotal.toLocaleString("en-US", {
						style: "currency",
						currency: "USD",
						minimumFractionDigits: 2,
					})}
				</p>
				<p>
					{grandAverage.toLocaleString("en-US", {
						style: "currency",
						currency: "USD",
						minimumFractionDigits: 2,
					})}
				</p>
			</div>
		</div>
	);
};

export default Ledger;
